import { ICalColorSet } from "@clockwise/client-commons/src/util/event-category-coloring";
import { bg_default, fg_onEmphasis } from "@clockwise/design-system/tokens";
import { animated, useSpring } from "@react-spring/web";
import classNames from "classnames";
import { noop } from "lodash";
import React, {
  CSSProperties,
  HTMLAttributes,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  bg_calAnnotation,
  bg_calDeemphasis,
  bg_calOwn,
  bg_calOwn_Emphasis,
  border_calAnnotation,
  border_calDeemphasis,
  border_calOwn,
  fg_calDeemphasis,
  fg_calOwn,
} from "../../../constants/calendarColorTokens";
import { CalendarColorSet, getOwnCalendarColor } from "../../../util/calendar-coloring";

export enum CalendarEventStatusEnum {
  "Feature" = "Feature",
  "Default" = "Default",
  "Possible" = "Possible",
  "Indeterminate" = "Indeterminate",
  "Vestige" = "Vestige",
  "Prototype" = "Prototype",
  "Before" = "Before",
  "Unknown" = "Unknown",
}

export type CalendarEventStatus = keyof typeof CalendarEventStatusEnum;

export enum CalendarEventVariantEnum {
  "SingleLine" = "SingleLine",
  "MultiLine" = "MultiLine",
}

export type CalendarEventVariant = keyof typeof CalendarEventVariantEnum;

export enum CalendarEventSpacingEnum {
  "None" = "None",
  "Full" = "Full",
}

export type CalendarEventSpacing = keyof typeof CalendarEventSpacingEnum;

export type Props = HTMLAttributes<HTMLDivElement> & {
  active?: boolean;
  annotation?: string;
  badge?: ReactNode;
  calendarColorSet: CalendarColorSet;
  deemphasis?: boolean;
  eventColorSet?: ICalColorSet;
  fade?: boolean;
  highlight?: boolean;
  onHighlightComplete?: () => void;
  spacing?: CalendarEventSpacing;
  status: CalendarEventStatus;
  stripes?: string[];
  subText?: string;
  text: string;
  variant?: CalendarEventVariant;
};

type State = {
  active: boolean;
  annotation: string | undefined;
  backgroundColor: string;
  badge: ReactNode | undefined;
  borderColor: string;
  deemphasis: boolean;
  fade: boolean;
  foregroundColor: string;
  highlight: boolean;
  onHighlightComplete: () => void;
  railColor: string;
  spacing: CalendarEventSpacing;
  status: CalendarEventStatus;
  stripes: string[];
  subText: string | undefined;
  text: string;
  variant: CalendarEventVariant;
};

const defaultEventContextState = {
  active: false,
  annotation: "",
  backgroundColor: bg_calOwn,
  badge: undefined,
  borderColor: border_calOwn,
  deemphasis: false,
  fade: false,
  foregroundColor: fg_calOwn,
  highlight: false,
  onHighlightComplete: noop,
  railColor: bg_calOwn_Emphasis,
  spacing: CalendarEventSpacingEnum.Full,
  status: CalendarEventStatusEnum.Default,
  stripes: [],
  subText: "",
  text: "",
  variant: CalendarEventVariantEnum.SingleLine,
};

const CalendarEventContext = createContext<State>(defaultEventContextState);

const defaultCalendarColorSet: CalendarColorSet = getOwnCalendarColor();

export const CalendarEvent = ({
  active,
  annotation,
  badge,
  calendarColorSet = defaultCalendarColorSet,
  deemphasis,
  eventColorSet,
  fade = false,
  highlight,
  onBlur,
  onClick,
  onFocus,
  onHighlightComplete,
  spacing,
  status,
  stripes,
  subText,
  text,
  variant,
}: Props) => {
  const rootRef = React.useRef<HTMLDivElement>(null);

  const handleKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      rootRef.current?.click();
    }
  };

  const handleMouseOver = () => {
    rootRef.current?.focus();
  };

  const handleMouseOut = () => {
    rootRef.current?.blur();
  };

  let railColor = border_calDeemphasis;
  let backgroundColor = bg_calDeemphasis;
  let foregroundColor = fg_calDeemphasis;
  let borderColor = border_calDeemphasis;

  if (!deemphasis) {
    railColor = calendarColorSet.border;
    backgroundColor = eventColorSet?.background || calendarColorSet.bg;
    foregroundColor = eventColorSet?.foreground || calendarColorSet.fg;
    borderColor = eventColorSet?.foreground || calendarColorSet.border;
  }

  switch (status) {
    case CalendarEventStatusEnum.Feature:
      backgroundColor = borderColor;
      foregroundColor = fg_onEmphasis;
      break;
    case CalendarEventStatusEnum.Vestige:
    case CalendarEventStatusEnum.Before:
    case CalendarEventStatusEnum.Prototype:
    case CalendarEventStatusEnum.Unknown:
      backgroundColor = bg_default;
      break;
    default:
      break;
  }

  const truncatedStripes = [...(stripes || defaultEventContextState.stripes)].slice(0, Stripes.max);

  return (
    <CalendarEventContext.Provider
      value={{
        active: active || defaultEventContextState.active,
        annotation: annotation || defaultEventContextState.annotation,
        backgroundColor,
        badge: badge || defaultEventContextState.badge,
        borderColor,
        deemphasis: deemphasis || defaultEventContextState.deemphasis,
        fade: fade || defaultEventContextState.fade,
        foregroundColor,
        highlight: highlight || defaultEventContextState.highlight,
        onHighlightComplete: onHighlightComplete || defaultEventContextState.onHighlightComplete,
        railColor,
        spacing: spacing || defaultEventContextState.spacing,
        status: status || defaultEventContextState.status,
        stripes: truncatedStripes,
        subText: subText || defaultEventContextState.subText,
        text: text || defaultEventContextState.text,
        variant: variant || defaultEventContextState.variant,
      }}
    >
      <div className="cw-h-full cw-w-full cw-bg-default cw-rounded ">
        <div
          className={classNames("cw-h-full cw-w-full cw-min-h-[10px]", {
            "cw-grayscale": deemphasis,
            "cw-opacity-50": fade,
          })}
          cw-id="calendar-event"
          ref={rootRef}
          onBlur={onBlur}
          onClick={onClick}
          onFocus={onFocus}
          onKeyUp={handleKeyUp}
          onMouseOut={handleMouseOut}
          onMouseOver={handleMouseOver}
        >
          <Container>
            <Annotation />
            <Badge />
            <Background>
              <Rail />
              <Stripes />
              <Layout>
                <Text>{text}</Text>
                <Text subText>{subText}</Text>
              </Layout>
            </Background>
          </Container>
        </div>
      </div>
    </CalendarEventContext.Provider>
  );
};

const Container = ({ children }: { children: ReactNode }) => {
  const { highlight, borderColor, onHighlightComplete } = useContext(CalendarEventContext);
  const [animationState, setAnimationState] = useState<"in" | "out" | "none">("none");

  useEffect(() => {
    if (highlight) {
      setAnimationState("in");
    }
  }, [highlight]);

  const animationInConfig = {
    from: { borderColor: "transparent" },
    to: { borderColor },
    config: { duration: 1000 },
    delay: 50,
    onResolve: () => setAnimationState("out"),
  };

  const animationOutConfig = {
    from: { borderColor },
    to: { borderColor: "transparent" },
    config: { duration: 1000 },
    delay: 4000,
    onResolve: () => {
      setAnimationState("none");
      onHighlightComplete();
    },
  };

  const animationNoneConfig = {
    from: { borderColor: "transparent" },
    to: { borderColor: "transparent" },
  };

  let animationConfig = null;
  switch (animationState) {
    case "in":
      animationConfig = animationInConfig;
      break;
    case "out":
      animationConfig = animationOutConfig;
      break;
    default:
      animationConfig = animationNoneConfig;
      break;
  }

  const style = useSpring(animationConfig);

  return (
    <animated.div
      className={classNames("cw-h-full cw-w-full cw-relative", "cw-border-solid cw-border")}
      style={style}
    >
      {children}
    </animated.div>
  );
};

const Annotation = () => {
  const { annotation } = useContext(CalendarEventContext);
  if (!annotation) return null;

  const style: CSSProperties = {
    backgroundColor: bg_calAnnotation,
    borderColor: border_calAnnotation,
  };

  return (
    <div
      className={classNames(
        "cw-absolute cw-top-[-24px]",
        "cw-leading-4 cw-overflow-hidden cw-text-clip cw-whitespace-nowrap",
        "cw-z-[41]",
        "cw-body-base",
        "cw-rounded cw-border-[1px] cw-border-solid",
        "cw-px-1 cw-py-[2px]",
        "cw-text-[12px] cw-leading-[13px]",
        "cw-font-medium",
        "cw-no-underline",
      )}
      style={style}
    >
      {annotation}
    </div>
  );
};

const Badge = () => {
  const { badge, backgroundColor, borderColor } = useContext(CalendarEventContext);

  if (!badge) return null;

  return (
    <div className={classNames("cw-absolute cw-top-[-10px] cw-right-[-10px]", "cw-z-40")}>
      <div
        className={classNames(
          "cw-h-[24px] cw-w-[24px]",
          "cw-flex cw-items-center cw-justify-center",
          "cw-rounded-full cw-border-1 cw-border-solid",
          "cw-overflow-hidden",
        )}
        style={{
          backgroundColor,
          borderColor,
        }}
      >
        {badge}
      </div>
    </div>
  );
};

const Background = ({ children }: { children: ReactNode }) => {
  const { backgroundColor, borderColor, status, spacing, active: isActive } = useContext(
    CalendarEventContext,
  );

  const color = backgroundColor;

  const isPrototpype = status === CalendarEventStatusEnum.Prototype;
  const isVestige = status === CalendarEventStatusEnum.Vestige;
  const isBefore = status === CalendarEventStatusEnum.Before;
  const isUnknown = status === CalendarEventStatusEnum.Unknown;

  const style: CSSProperties = {
    backgroundColor: color,
    // border should be solid & transparent when set to none to avoid layout shifts
    borderColor:
      isPrototpype || isVestige || isBefore || isUnknown || isActive ? borderColor : "transparent",
    borderStyle: isBefore || ((isVestige || isUnknown) && !isActive) ? "dashed" : "solid",
  };

  return (
    <div
      className={classNames(
        "cw-h-full cw-w-full",
        "cw-body-base",
        "cw-rounded cw-border-[1px]",
        "cw-cursor-pointer",
        "cw-box-border",
        "cw-border-l-0",
        "cw-pl-[8px]",
        {
          "cw-py-1": spacing === CalendarEventSpacingEnum.Full,
          "cw-py-0": spacing === CalendarEventSpacingEnum.None,
        },
        isActive ? "cw-drop-shadow" : "cw-drop-shadow-none",
      )}
      style={style}
    >
      {children}
    </div>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { variant, stripes } = useContext(CalendarEventContext);

  const isCompact = variant === CalendarEventVariantEnum.SingleLine;

  return (
    <div
      className="cw-flex cw-space-x-1 cw-h-full cw-w-full cw-overflow-hidden"
      style={{
        paddingLeft: stripes.length * Stripes.fullWidth,
        minHeight: 12,
        marginTop: isCompact ? -1 : 2,
      }}
    >
      <div
        className={classNames(
          "cw-flex",
          variant === CalendarEventVariantEnum.SingleLine
            ? "cw-flex-row cw-items-start"
            : "cw-flex-col",
          variant === CalendarEventVariantEnum.SingleLine ? "cw-space-x-1" : "cw-space-y-0",
          variant === CalendarEventVariantEnum.SingleLine
            ? "cw-whitespace-nowrap"
            : "cw-whitespace-normal",
        )}
      >
        {children}
      </div>
    </div>
  );
};

const Rail = () => {
  const { railColor, status, active: isActive, stripes } = useContext(CalendarEventContext);

  const style: CSSProperties = {
    backgroundColor: railColor,
    borderColor: railColor,
  };

  const isPossible = status === CalendarEventStatusEnum.Possible;
  const isPrototype = status === CalendarEventStatusEnum.Prototype;
  const isVestige = status === CalendarEventStatusEnum.Vestige;
  const isBefore = status === CalendarEventStatusEnum.Before;
  const isIndeterminate = status === CalendarEventStatusEnum.Indeterminate;
  const isUnknown = status === CalendarEventStatusEnum.Unknown;
  const hasStripes = stripes.length > 0;
  const hasOuterBorder = isActive || isPrototype || isVestige || isBefore || isUnknown;

  if (isPossible) {
    style.backgroundColor = undefined;
    style.background = `repeating-linear-gradient( 45deg, #FFFFFF, #FFFFFF 1px, ${railColor} 0px, ${railColor} 4px)`;
    style.borderColor = railColor;
  }
  if (isVestige) {
    style.borderColor = railColor;
    style.backgroundColor = "transparent";
  }
  if (isBefore) {
    style.borderColor = railColor;
    style.backgroundColor = "transparent";
  }
  if (isIndeterminate) {
    style.borderColor = railColor;
    style.backgroundColor = "transparent";
  }
  if (isUnknown) {
    style.borderColor = railColor;
    style.backgroundColor = "transparent";
  }

  return (
    <div
      className={classNames(
        "cw-absolute cw-top-[-1px] cw-bottom-[-1px] cw-left-0",
        "cw-z-[25]",
        "cw-w-[4px]",
        "cw-border cw-border-solid cw-rounded-l",
        { "cw-border-r-0": !hasStripes && (isUnknown || isVestige) },
        { "cw-border-dashed": !isActive && !isPossible },
        { "cw-border-solid": isActive || isPossible },
        { "cw-border-l-1": hasOuterBorder },
      )}
      style={style}
    >
      {/* rail - intentionally empty */}
    </div>
  );
};

const Stripes = () => {
  const { stripes } = useContext(CalendarEventContext);

  if (!stripes) return null;

  return (
    <div
      className={classNames("cw-absolute cw-top-[-1px] cw-bottom-[-1px] cw-left-[4px]")}
      style={{
        width: stripes.length * Stripes.fullWidth,
      }}
    >
      <div className="cw-flex cw-h-full cw-w-full">
        {stripes.map((color) => (
          <div
            key={`color-${color}`}
            className="cw-h-full"
            style={{
              backgroundColor: color,
              width: Stripes.width,
              marginLeft: Stripes.marginLeft,
            }}
          />
        ))}
      </div>
    </div>
  );
};
Stripes.width = 4;
Stripes.marginLeft = 1;
Stripes.max = 3;
Stripes.fullWidth = Stripes.width + Stripes.marginLeft;

const Text = ({ children, subText = false }: { children?: ReactNode; subText?: boolean }) => {
  const { foregroundColor, status } = useContext(CalendarEventContext);
  if (!children) return null;

  const color = foregroundColor;
  const isVestige = status === CalendarEventStatusEnum.Vestige;

  return (
    <div
      className={classNames(
        "cw-body-base",
        "cw-leading-[13px]",
        subText ? "cw-text-[11px]" : "cw-text-[12px]",
        subText ? "" : "cw-font-medium",
        isVestige ? "cw-line-through" : "",
      )}
      style={{ color }}
    >
      {children}
    </div>
  );
};
